import React, {
  useCallback, useId, useMemo, useState,
} from 'react';

import Icon from 'components/DesignSystem/Icon';
import FilterField from 'components/Flux/Personalized/MyStudies/FilterBar/FilterField';
import OptionsPopover, { OptionsPopoverStatus } from 'components/Flux/Personalized/MyStudies/FilterBar/OptionsPopover';
import {
  buildCompanyOptions,
  companyFilterLabel,
  companySearchPlaceholder,
} from 'helper/flux/myStudiesFilterOptions';
import { isValidUserId } from 'helper/flux/myStudyRow';
import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import useStudiesErrorState, { StudiesErrorKind } from 'utils/hooks/flux/personalized/useStudiesErrorState';
import {
  MyStudiesCompanyFilter,
  MyStudiesFiltersChange,
} from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

/** Falha ao carregar as empresas, por tipo (`useStudiesErrorState`, modo `auxiliary`). */
const ERROR_MESSAGES: Readonly<Record<StudiesErrorKind, string>> = {
  network: 'Não foi possível carregar as empresas. Verifique sua conexão.',
  server: 'Não foi possível carregar as empresas. Tente de novo em instantes.',
  forbidden: 'Você não tem acesso à lista de empresas.',
};

export interface CompanyFilterProps {
  /** `filters.empresa`. */
  value: MyStudiesCompanyFilter;
  /** Usuário logado (`selectUserId`): sem usuário válido (sem JWT), a lista não é pedida. */
  viewerUserId: number;
  /**
   * `setFilters` da tela (`useMyStudiesFilters`), como no `MigrationNotice`: recebe
   * `{ empresa }`. Trocar de empresa com um usuário escolhido em Criado por volta para Todos
   * dentro do `applyFilterChange` (.dc.html:666).
   */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
}

/**
 * Filtro Empresa da barra de Meus Estudos (#2000, .dc.html:154-170), só para admin: a casca nem
 * o monta para cliente, e a lista de empresas só é pedida com ele montado.
 *
 * - Botão: "Todas as empresas", "Casos Ampere" (id 1) ou o nome da empresa; com empresa
 *   escolhida, a borda azul de filtro ativo (.dc.html:715). Aberto, a borda não muda: na barra,
 *   o azul quer dizer "filtro ativo".
 * - Lista (D15): "Todas as empresas" e "Casos Ampere" fixos no topo, depois as demais sem repetir
 *   o id 1, com a contagem histórica; busca "Buscar entre N empresas" no nome e no subtítulo.
 *   Carregando ou com erro, os dois fixos continuam escolhíveis (sem contagem), junto da mensagem.
 * - Escolher grava `empresa=<id>` na URL (o nome nunca vai para a URL).
 * - Erros (#1984): `useStudiesErrorState(error, { mode: 'auxiliary' })`. Rede, servidor e 403
 *   que não é de sessão ficam na lista, como mensagem; a barra e a lista de estudos seguem.
 *   Durante o "Tentar de novo" (`isFetching`), a mensagem volta a ser a de carregando.
 *
 * A lista é pedida ao montar (e não ao abrir): o placeholder precisa do total e o botão, do nome
 * da empresa vinda da URL. É uma requisição leve, e o cache do RTK Query a reaproveita. Sem
 * usuário válido (sem JWT), não é pedida, como a lista de estudos.
 */
export default function CompanyFilter({
  value,
  viewerUserId,
  onFiltersChange,
}: CompanyFilterProps) {
  const { api } = useMyStudiesProduct();
  const nameId = useId();
  const toggleId = useId();
  const [open, setOpen] = useState(false);
  const {
    currentData, error, isFetching, refetch,
  } = api.useCompaniesQuery(undefined, { skip: !isValidUserId(viewerUserId) });
  const errorKind = useStudiesErrorState(error, { mode: 'auxiliary' });

  // Sem a resposta (carregando ou erro), só os dois fixos
  const options = useMemo(() => buildCompanyOptions(currentData), [currentData]);
  const selectedKey = value === 'todas' ? 'todas' : String(value.companyId);
  // O pedido do "Tentar de novo" não limpa o `error` (RTK Query 1.9): `isFetching` vence
  let status: OptionsPopoverStatus = 'loading';
  if (errorKind && !isFetching) status = 'error';
  else if (currentData) status = 'ready';

  const handleSelect = useCallback((key: string) => {
    const chosen = options.find((option) => option.key === key);
    if (chosen) onFiltersChange({ empresa: chosen.value });
  }, [options, onFiltersChange]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <FilterField
      label={(
        <>
          <span id={nameId}>EMPRESA</span>
          {' '}
          <span className="my-studies-company-filter-note">· somente admin</span>
        </>
      )}
      className="my-studies-company-filter"
    >
      <OptionsPopover
        open={open}
        onOpenChange={setOpen}
        toggleId={toggleId}
        // Nome "EMPRESA <empresa>", como o "STATUS <seleção>" do StatusFilter
        toggleAriaLabelledBy={`${nameId} ${toggleId}`}
        toggleClassName={`my-studies-company-toggle${value === 'todas' ? '' : ' is-filtered'}`}
        toggleContent={(
          <>
            <span className="my-studies-company-toggle-label">{companyFilterLabel(value, currentData)}</span>
            <span className="my-studies-company-toggle-chevron" aria-hidden="true">
              <Icon iconType="down_arrow" color="currentColor" size="small" />
            </span>
          </>
        )}
        menuClassName="my-studies-company-menu"
        searchLabel="Buscar empresa"
        searchPlaceholder={companySearchPlaceholder(currentData)}
        listLabel="Empresas"
        options={options}
        selectedKey={selectedKey}
        onSelect={handleSelect}
        status={status}
        loadingMessage="Carregando empresas…"
        emptyMessage="Nenhuma empresa encontrada."
        errorMessage={errorKind ? ERROR_MESSAGES[errorKind] : undefined}
        onRetry={errorKind === 'forbidden' ? undefined : retry}
      />
    </FilterField>
  );
}
